/**
 * The typeahead cache provides the ability for a response to check
 * if it is the latest in a response chain.
 */

//  -----------------------------------
//  Private vars/functions
//  -----------------------------------
const DOES_NOT_EXIST = -1;

//  -----------------------------------
//  TYPES
//  -----------------------------------

/**
 * An object that contains a meta property with a
 * monotonically increasing sequence number.
 */
type Sequencable = {
  meta: {
    sequenceNumber: number;
  };
};

/**
 * Function that takes an object and returns
 * a key that will be used to access
 */
type CacheHashFunction<X> = (item: X) => string;

/**
 * A counting mechanism that takes
 * a sequencable and keeps track of the sequenceNumber.
 */
type Ledger<X extends Sequencable> = {
  /**
   * Updates a ledger if the item is the latest.
   * @param item
   */
  setMax: (item: X) => void;
  /**
   * Retrieve the maximum value from the ledger.
   */
  getMax: (item: X) => number;
  /**
   * Test an item to see if it is the latest value.
   */
  isMax: (item: X) => boolean;
};

/**
 * A cache to store json objects.
 */
type CacheAPI<X> = {
  /**
   * Destroys a cache.  This is primarily used in testing.
   * @param identifier
   */
  destroy: () => void;
  /**
   * Predicate method to test if an item is the latest item
   * seen in a cache.
   * @param item
   * @returns
   */
  isLatest: (item: X) => boolean;
  /**
   * This function always returns an item from the cache,
   * regardless of whether it is first item in the cache
   * or not.
   * @param item
   * @returns the latest version of that item.
   */
  getLatest: (item: X) => X;
};

/**
 * Interface for cache stores to save and retrieve.
 */
type CacheStorageEngine<X> = {
  save: (item: X) => void;
  fetch: (item: X) => X;
};

/**
 * A structure that will retrieve a POJO
 * from a number.  The searchSequenceNumber will be
 * furnished by the ledger.
 */
type MemoryResponseMap<X> = {
  [searchSequenceNumber: number]: X;
};

type ResponseCache<X extends Sequencable> = {
  [namespace: string]: MemoryResponseMap<X>;
};

type LedgerMap<X extends Sequencable> = {
  [namespace: string]: Ledger<X>;
};

//  -----------------------------------
//  Ledger
//  -----------------------------------

const ledgers: LedgerMap<any> = {};

const ledgerFactory = <T extends Sequencable>(
  namespace: string,
  hashFn: CacheHashFunction<T>
) => {
  const ledger: Ledger<T> = {
    setMax: (item: T) => {
      const identifier = hashFn(item);
      const ledgerMax = ledgers[namespace]?.[identifier] ?? DOES_NOT_EXIST;

      const max = Math.max(ledgerMax, item.meta.sequenceNumber);

      if (max > item.meta.sequenceNumber) {
        throw new Error(
          `max: ${max} already higher than ${item.meta.sequenceNumber}`
        );
      }
      ledgers[namespace][identifier] = max;
    },
    getMax: (item: T) => {
      const identifier = hashFn(item);
      if (!(identifier in ledgers[namespace])) {
        return DOES_NOT_EXIST;
      }
      return ledgers[namespace][identifier];
    },

    isMax: (item: T) => {
      const identifier = hashFn(item);

      if (!(identifier in ledgers[namespace])) {
        ledgers[namespace][identifier] = item.meta.sequenceNumber;
        return true;
      }

      return (
        Math.max(ledgers[namespace][identifier], item.meta.sequenceNumber) <=
        item.meta.sequenceNumber
      );
    }
  };
  ledgers[namespace] = ledger;
  return ledger;
};

//  -----------------------------------
//  Memory Cache
//  There's really only cache implementation and that's memory backed for now.
//  In the future, we may have caches which are backed by local storage or remote.
//  -----------------------------------

const memoryCacheMap: ResponseCache<any> = {};

const ensureCache = (namespace: string, identifier: string) => {
  memoryCacheMap[namespace] = memoryCacheMap[namespace] || {};
  memoryCacheMap[namespace][identifier] =
    memoryCacheMap[namespace][identifier] || {};
};

const memoryCacheFactory = <T extends Sequencable>(
  namespace: string,
  hashFunction: CacheHashFunction<T>,
  ledger: Ledger<T>
): CacheStorageEngine<T> => {
  return {
    save: (item: T) => {
      const identifier = hashFunction(item);
      ensureCache(namespace, identifier);
      memoryCacheMap[namespace][identifier][item.meta.sequenceNumber] = item;
    },
    fetch: (item: T) => {
      const identifier = hashFunction(item);
      ensureCache(namespace, identifier);
      const max = ledger.getMax(item);
      return memoryCacheMap[namespace][identifier][max];
    }
  };
};

//  -----------------------------------
//  Public API
//  -----------------------------------

const caches = {};

/**
 * Retrieves or builds a cache for a namespace if not present.
 * @param cacheNamespace A signifier for the managed responses
 * @param hashFn {CacheHashFunction}
 * @returns {CacheAPI}
 */
export const getCache = <T extends Sequencable>(
  cacheNamespace: string,
  hashFn: CacheHashFunction<T>
): CacheAPI<T> => {
  if (!caches[cacheNamespace]) {
    const ledger = (ledgers[cacheNamespace] = ledgerFactory<T>(
      cacheNamespace,
      hashFn
    ));
    const memoryCache = memoryCacheFactory<T>(cacheNamespace, hashFn, ledger);

    const set = (item: T): void => {
      // save all responses
      memoryCache.save(item);

      if (ledger.isMax(item)) {
        ledger.setMax(item);
      }
    };

    const cache: CacheAPI<T> = {
      isLatest: (item: T): boolean => {
        return ledger.isMax(item);
      },
      getLatest: (item: T): T => {
        set(item);
        const latest = memoryCache.fetch(item) ?? item;
        return latest ? latest : item;
      },
      // exposed for convenience/testing
      destroy: () => {
        delete caches[cacheNamespace];
      }
    };

    caches[cacheNamespace] = cache;
  }

  return caches[cacheNamespace];
};
